import KODIAK_REWARDS_ABI from 'abis/KodiakRewards.json'
import { KODIAK_REWARDS_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import { useCallback, useMemo, useState } from 'react'
import { useIsTransactionPending, useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi } from 'viem'

interface UseHarvestRewardsParams {
  hasRewards: boolean
}

export function useHarvestRewards({ hasRewards }: UseHarvestRewardsParams) {
  const { chainId, account, wagmiPublicClient } = useConnection()
  const { writeContractAsync } = useWriteContract()
  const [harvesting, setHarvesting] = useState(false)
  const [harvestHash, setHarvestHash] = useState<string | null>(null)
  const isHarvestPending = useIsTransactionPending(harvestHash ?? undefined)

  const addTransaction = useTransactionAdder()
  const rewardsAddress = useMemo(() => (chainId ? KODIAK_REWARDS_ADDRESSES[chainId] : undefined), [chainId])

  const canHarvest = Boolean(hasRewards && rewardsAddress && account && !harvestHash)

  const harvest = useCallback(async () => {
    if (!rewardsAddress || !wagmiPublicClient || !account) {
      return false
    }

    setHarvesting(true)

    try {
      const tx = {
        address: rewardsAddress,
        abi: KODIAK_REWARDS_ABI as Abi,
        functionName: 'harvestAllRewards',
        account,
      }

      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const hash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })

      setHarvestHash(hash)
      setHarvesting(false)

      addTransaction({ hash }, { type: TransactionType.DIVIDENDS_HARVEST })

      return true
    } catch (error) {
      setHarvesting(false)
      console.error('Error harvesting rewards:', error)
      return false
    }
  }, [rewardsAddress, wagmiPublicClient, account, addTransaction, writeContractAsync])

  const resetHarvestState = useCallback(() => {
    setHarvestHash(null)
    setHarvesting(false)
  }, [])

  return {
    harvest,
    harvesting,
    harvestHash,
    isHarvestPending,
    canHarvest,
    resetHarvestState,
    isHarvested: !!harvestHash && !isHarvestPending,
  }
}
